import React, { useState, useEffect, useMemo, memo, useRef } from "react"
import { Tabs } from "antd"
import Modal from "../../Modal/Modal"
import UserPresets from "./UserPresets/UserPresets"
import PrimePresets from "./PrimePresets/PrimePresets"

//modals
import AddPreset from "./modalContent/AddPreset"
import UpdatePreset from "./modalContent/UpdatePreset"
import DeletePreset from "./modalContent/DeletePreset"
import DeletePresetSuccess from "./modalContent/DeletePresetSuccess"
import "./Presets.scss"

// apis
// admin presets
import { useGetPresets } from "api/hooks/admin/usePresetsApi"
// user presets
import {
  useGetGridPresets,
  usePostGridPreset,
  usePutGridPreset,
  useDeleteGridPreset,
} from "api/hooks/grids/usePresetsApi"

// utils
import { getGridPresetIdent } from "components/Grid/Presets/presetUtils"

const Presets = ({
  api,
  gridName, // grid link from all-reports API
  apiConfig, // includes requestTypeId
}) => {
  // eg, "reports-request-34"
  const gridPresetIdent = React.useMemo(() => {
    return getGridPresetIdent(gridName, apiConfig)
  }, [gridName, apiConfig])

  const { data: gridPresets = [], isLoading: isGridPresetsLoading } = useGetGridPresets(gridPresetIdent)
  const { data: allAdminPresets = [] } = useGetPresets()
  const postGridPreset = usePostGridPreset(gridPresetIdent)
  const putGridPreset = usePutGridPreset(gridPresetIdent)
  const deleteGridPreset = useDeleteGridPreset(gridPresetIdent)

  const [view, setView] = useState("user") // TODO: not necessary since set function not used

  const initLoad = useRef(true)

  const [activeTab, setActiveTab] = React.useState()

  // filter to only current grid presets
  const adminGridPresets = useMemo(() => {
    if (allAdminPresets && Array.isArray(allAdminPresets)) {
      return allAdminPresets.filter((preset) => preset.grid_ident === gridName)
    }
    return []
  }, [gridName, allAdminPresets])

  const [openModal, setOpenModal] = useState(false)
  const [modalConfig, setModalConfig] = useState({})
  const [activePreset, setActivePreset] = useState(null)

  /** get current state */

  const deepEqual = (obj1, obj2) => {
    // Check if both parameters are objects
    if (obj1 && obj2 && typeof obj1 === "object" && typeof obj2 === "object") {
      // Get keys of both objects
      const keys1 = Object.keys(obj1)
      const keys2 = Object.keys(obj2)

      // Check if the number of keys is the same
      if (keys1.length !== keys2.length) {
        return false
      }

      // Check if all keys in obj1 exist in obj2
      for (let key of keys1) {
        if (!keys2.includes(key)) {
          return false
        }
      }

      // Recursively compare values
      for (let key of keys1) {
        if (!deepEqual(obj1[key], obj2[key])) {
          return false
        }
      }

      // If all checks pass, the objects are deep equal
      return true
    } else {
      // If not both objects, compare values directly
      return obj1 === obj2
    }
  }

  const findObjectByKey = (o, key) => {
    if (!o || typeof o === "string") {
      //  assumes all leaves will be strings
      return o
    }
    o = Object.assign({}, o) // make a shallow copy
    if (o[key]) {
      delete o[key] // delete key if found
    }
    Object.keys(o).forEach((k) => {
      o[k] = findObjectByKey(o[k], key) // do the same for children
    })
    return o // return the new object
  }

  const findActiveState = (currentState) => {
    let presets = view === "user" ? [...gridPresets] : [...adminGridPresets]

    let foundActivePreset = presets.find((preset) => {
      let foundPreset = deepEqual(findObjectByKey(preset.json, "width"), findObjectByKey(currentState, "width"))
      return foundPreset
    })

    if (foundActivePreset) setActivePreset(foundActivePreset)
    else setActivePreset(null)
  }

  const getSortModel = () => {
    const columnState = api?.getColumnState()
    const value = columnState?.find((s) => s.sort != null)

    if (value) {
      return [value]
    } else {
      return []
    }
  }

  const getCurrentState = () => {
    if (api) {
      const currentState = {
        column_state: api.getColumnState(),
        filter_state: api.getFilterModel(),
        sort_state: getSortModel(),
      }
      findActiveState(currentState)
    }
  }

  useEffect(() => {
    if (gridPresets && api) {
      getCurrentState()
      const eventTypes = [
        "columnVisible",
        "columnPinned	",
        "columnResized",
        "columnMoved",
        "columnRowGroupChanged",
        "displayedColumnsChanged",
        "sortChanged",
        "filterChanged",
      ]
      eventTypes.forEach((eventType) => {
        api.addEventListener(eventType, () => {
          getCurrentState()
        })
      })
    }
  }, [gridPresets, api])

  useEffect(() => {
    if (initLoad.current && isGridPresetsLoading === false) {
      gridPresets.forEach((preset) => {
        if (preset.is_default) {
          onLoad(preset)
          initLoad.current = false
        }
      })
    }
  }, [isGridPresetsLoading])

  /** API functions */

  const savePreset = (name) => {
    let body = {
      name,
      grid_ident: gridPresetIdent,
      json: {
        column_state: api.getColumnState(),
        filter_state: api.getFilterModel(),
        sort_state: getSortModel(),
      },
      is_default: false,
    }
    postGridPreset(
      { body },
      {
        onSuccess: () => {
          setOpenModal(false)
        },
      }
    )
  }

  const updatePreset = (preset) => {
    const req = {
      ...preset,
      preset_id: preset.id,
    }
    putGridPreset(
      { body: req },
      {
        onSuccess: () => {
          setOpenModal(false)
        },
      }
    )
  }

  const deletePreset = (deletedPreset) => {
    deleteGridPreset(
      {
        params: {
          "preset-id": deletedPreset.id,
        },
      },
      {
        onSuccess: (resp) => {
          onOpenModal("delete-preset-success", deletedPreset)
        },
      }
    )
  }

  const setDefaultPreset = (preset) => {
    gridPresets.forEach((preset) => {
      if (preset.is_default) {
        updatePreset({
          ...preset,
          is_default: false,
        })
      }
    })
    const index = gridPresets.findIndex((gp) => gp.id === preset.id)
    let updatedPreset = gridPresets[index]
    updatedPreset.is_default = true
    updatePreset(updatedPreset)
  }

  const formatFilterState = (filterState) => {
    let updatedFilterState = { ...filterState }
    Object.keys(updatedFilterState).forEach((key) => {
      let colFilter = updatedFilterState[key]
      Object.keys(colFilter).forEach((colFilterKey) => {
        if (colFilterKey === "conditions" && !Array.isArray(colFilter[colFilterKey])) {
          let conditionsArr = []
          Object.values(colFilter[colFilterKey]).forEach((value) => {
            conditionsArr.push(value)
          })
          colFilter[colFilterKey] = conditionsArr
        }
      })
    })

    return updatedFilterState
  }

  const onLoad = (preset) => {
    const { column_state, filter_state } = preset.json
    if (column_state) api.applyColumnState({ state: column_state, applyOrder: true })
    if (filter_state) api.setFilterModel(formatFilterState(filter_state))

    setActivePreset(preset)
  }

  const onResetPreset = () => {
    api.setFilterModel(null)
    api.resetColumnState()
    api.resetColumnGroupState()
    api.onFilterChanged()
  }

  const onOpenModal = (variant, data) => {
    switch (variant) {
      case "add-preset":
        setModalConfig({
          title: "Save Preset",
          children: (
            <AddPreset
              setOpenModal={setOpenModal}
              presets={[...allAdminPresets, ...gridPresets]}
              savePreset={savePreset}
            />
          ),
        })
        setOpenModal(true)
        break
      case "update-preset":
        setModalConfig({
          title: "Update Preset",
          children: (
            <UpdatePreset
              setOpenModal={setOpenModal}
              data={data}
              presets={[...allAdminPresets, ...gridPresets]}
              updatePreset={updatePreset}
            />
          ),
        })
        setOpenModal(true)
        break
      case "delete-preset":
        setModalConfig({
          title: `Delete ${data.name}`,
          type: "delete",
          message: "Are you sure you would like to delete preset? This cannot be undone.",
          children: (
            <DeletePreset
              onOpenModal={onOpenModal}
              deletePreset={deletePreset}
              setOpenModal={setOpenModal}
              data={data}
            />
          ),
        })
        setOpenModal(true)
        break
      case "delete-preset-success":
        setModalConfig({
          label: "Preset Deleted",
          title: `Successfully deleted ${data.name}`,
          children: <DeletePresetSuccess setOpenModal={setOpenModal} />,
        })
        setOpenModal(true)
        break
      default:
        return
    }
  }

  const tabItems = [
    {
      key: "prime",
      label: "Prime Presets",
      children: (
        <PrimePresets
          presets={adminGridPresets}
          activePreset={activePreset}
          agGridApi={api}
          onLoad={onLoad}
          onOpenModal={onOpenModal}
          onResetPreset={onResetPreset}
          setDefaultPreset={setDefaultPreset}
        />
      ),
    },
    {
      key: "user",
      label: "My Presets",
      children: (
        <UserPresets
          presets={gridPresets?.filter((preset) => preset.preset_type === "user")}
          activePreset={activePreset}
          agGridApi={api}
          onLoad={onLoad}
          setDefaultPreset={setDefaultPreset}
          updatePreset={updatePreset}
          onResetPreset={onResetPreset}
          onOpenModal={onOpenModal}
        />
      ),
    },
  ]

  return (
    <div className="grid-presets">
      <Tabs
        items={tabItems}
        activeKey={activeTab}
        onChange={setActiveTab}
        centered
      />
      <Modal
        className="preset-modal"
        type={modalConfig.type}
        open={openModal}
        onCancel={() => setOpenModal(false)}
        title={modalConfig.title}
        message={modalConfig.message}
        setOpenModal={setOpenModal}
        size="sm"
        footer={[]}
      >
        {modalConfig.children}
      </Modal>
    </div>
  )
}

export default memo(Presets)
